/**
 * Stream-while-recording: send a take's bytes to its resumable session
 * **while the take is still being recorded**, so that tapping Finish leaves
 * only the tail to upload rather than the whole video.
 *
 * ## What this is, and what it deliberately is not
 *
 * It is an **optimisation layered in front of** the post-record upload, never
 * a replacement for it. The post-record run (`reflectionUploadTask`) still
 * owns every guarantee the upload makes — bounded retry, the probe-and-resume
 * loop, failure classification, finalize. This module only gets a head start
 * on the same session, and anything that goes wrong with it quietly hands the
 * whole job back:
 *
 * - a chunk that fails or times out stops streaming; the post-record run
 *   opens on that session with a probe and carries on from what the server
 *   holds;
 * - a session that is gone, refused, or answered in a way that makes no sense
 *   is dropped; the post-record run mints a fresh one and uploads the whole
 *   recording, exactly as it does for a take that never streamed.
 *
 * So no failure here can lose a recording, and none of them is ever shown to
 * the user. That is also why it has no retry of its own: a retry loop that
 * runs while the camera is still rolling would be a second copy of the one in
 * `resumable.ts`, and the post-record run is strictly better placed to retry
 * because by then it knows the total.
 *
 * It does **not** drop bytes once they are committed, by design: the whole
 * recording stays in the tab so Retry can re-upload it under a fresh session,
 * and so the fallbacks above have something to fall back with. Memory use is
 * that of a take that never streamed; the win is time.
 *
 * ## The protocol it relies on
 *
 * Every chunk streamed here goes out as `bytes a-b/*` — GCS's form for a chunk
 * of an upload whose total is not yet known — against a session minted with
 * no declared length. GCS insists every such chunk be a whole number of
 * 256 KiB blocks, so only chunk sizes that are get streamed at all.
 *
 * It sends a chunk only once **more** than a chunk's worth is buffered past
 * the committed offset, never exactly a chunk's worth. That keeps at least one
 * byte back for the post-record run's final `bytes a-b/total` PUT, which is the
 * request that tells the server the upload is over. Were every byte streamed,
 * the upload could only be closed by a probe that names the total, and that
 * would be a second way to finish an upload for no gain.
 *
 * ## Mobile Safari
 *
 * The bytes streamed are the recorder's `dataavailable` chunks, in order, and
 * the recording the post-record run is handed is `new Blob(sameChunks)`. The
 * streamed prefix is therefore byte-identical to the file, however the
 * container is laid out, and nothing about it depends on iOS emitting a
 * seekable or self-contained fragment.
 */
import { GCS_RESUMABLE_CHUNK_ALIGNMENT_BYTES, type UploadContentType } from "@reflect/domain";

import type { ChunkTransport, CreateResumableSessionResult } from "./types";
import { UploadAbortedError } from "./types";

/** Everything {@link startLiveUpload} needs for one take. */
export interface LiveUploadParams {
  /** The normalized MIME type the session is minted for. */
  contentType: UploadContentType;
  /**
   * Mint the session, with no declared length. One attempt: a failure here
   * only means the post-record run mints its own, with its own retries and
   * its own user-facing refusal copy (a quota refusal included).
   */
  createSession: () => Promise<CreateResumableSessionResult>;
  /** Bytes per streamed chunk. Streaming is skipped unless it is 256 KiB-aligned. */
  chunkSizeBytes: number;
  /**
   * The caller's byte cap. Past it nothing more is streamed: the post-record
   * pre-flight refuses the recording anyway, and sending bytes it is certain
   * to refuse spends a phone's data on nothing.
   */
  maxUploadBytes: number;
  /** Per-request timeout, forwarded to every PUT. */
  timeoutMs: number;
  /** Progress-idle window, forwarded to every PUT. */
  stallTimeoutMs: number;
  /** The transport binding (XHR in production, a fake in tests). */
  transport: ChunkTransport;
}

/**
 * The session a finished stream hands to the post-record run.
 *
 * `committed` is the offset the server last acknowledged, or `null` when the
 * last request ended without a legible answer and the run must probe before it
 * writes.
 */
export interface LiveUploadHandoff {
  /** The session URI the streamed bytes went to. */
  uploadUrl: string;
  /** The pending object key `finalize` is called with. */
  key: string;
  /** The content type the session was minted for. */
  contentType: UploadContentType;
  /** Server-acknowledged offset, or `null` to probe first. */
  committed: number | null;
}

/** A stream in progress, driven by the recorder and closed by the upload task. */
export interface LiveUpload {
  /**
   * Feed one `dataavailable` chunk. Must be called with exactly the chunks the
   * final recording is assembled from, in the same order.
   */
  append(chunk: Blob): void;
  /**
   * Stop streaming, wait for the request in flight to settle, and hand the
   * session over — or `null` when there is nothing worth handing over and the
   * post-record run should start from scratch. Idempotent: later calls resolve
   * to the same answer.
   */
  close(): Promise<LiveUploadHandoff | null>;
  /** Cancel the request in flight and drop the session. For a take thrown away. */
  abandon(): void;
}

/**
 * Whether `chunkSizeBytes` can be streamed. GCS 400s any non-final chunk that
 * is not a whole number of 256 KiB blocks, and every streamed chunk is
 * non-final. The local e2e `?chunkBytes=` seam takes arbitrary sizes, so this
 * is a real branch rather than a formality.
 *
 * @param chunkSizeBytes - the chunk size the run would use.
 */
export function canStreamChunkSize(chunkSizeBytes: number): boolean {
  return (
    Number.isInteger(chunkSizeBytes) &&
    chunkSizeBytes > 0 &&
    chunkSizeBytes % GCS_RESUMABLE_CHUNK_ALIGNMENT_BYTES === 0
  );
}

/**
 * Begin streaming one take. The session is requested immediately; chunks are
 * sent as {@link LiveUpload.append} buffers enough of them.
 *
 * @param params - see {@link LiveUploadParams}.
 */
export function startLiveUpload(params: LiveUploadParams): LiveUpload {
  const { chunkSizeBytes, maxUploadBytes, transport, timeoutMs, stallTimeoutMs } = params;
  const controller = new AbortController();
  const chunks: Blob[] = [];
  let buffered = 0;
  /** Server-acknowledged offset; `null` once the last answer was not legible. */
  let committed: number | null = 0;
  /** No more chunks will be sent: closed, abandoned, or streaming gave up. */
  let stopped = false;
  /** The session is unusable and must not be handed over. */
  let dead = false;
  let session: CreateResumableSessionResult | null = null;
  /** The drain loop in flight, if any — `close()` waits on it. */
  let draining: Promise<void> | null = null;
  let closing: Promise<LiveUploadHandoff | null> | null = null;

  void params.createSession().then(
    (created) => {
      // Too late to be used once the stream has stopped (see `close()`).
      if (stopped) return;
      session = created;
      kick();
    },
    () => {
      // No session, nothing streamed. The post-record run mints its own.
      dead = true;
      stopped = true;
    },
  );

  /** Whether a chunk can go out now: more than one chunk's worth is waiting. */
  function ready(): boolean {
    return !stopped && committed !== null && buffered - committed > chunkSizeBytes;
  }

  /** Send full chunks while {@link ready}. */
  async function drain(url: string): Promise<void> {
    while (ready() && committed !== null) {
      if (committed + chunkSizeBytes > maxUploadBytes) {
        stopped = true;
        return;
      }
      const start = committed;
      const end = start + chunkSizeBytes; // exclusive
      let res;
      try {
        res = await transport({
          url,
          body: new Blob(chunks).slice(start, end),
          // `end - 1`: Content-Range is inclusive at both ends. The star says
          // the total is not known yet, which is the whole point.
          contentRange: `bytes ${start}-${end - 1}/*`,
          timeoutMs,
          stallTimeoutMs,
          signal: controller.signal,
        });
      } catch {
        // Only an abort rejects, and only `abandon()` aborts.
        dead = true;
        stopped = true;
        return;
      }
      switch (res.kind) {
        case "incomplete": {
          const n = res.nextOffset;
          if (!Number.isInteger(n) || n < start || n > end) {
            // An offset outside what was sent is not a session to build on.
            dead = true;
            stopped = true;
            return;
          }
          committed = n;
          // No forward progress is not an error, but it is a reason to stop
          // spending data mid-take; the post-record run retries with backoff.
          if (n === start) stopped = true;
          continue;
        }
        case "retryable":
        case "offset-unknown":
          // The session is probably fine and the server's offset is unknown.
          // The post-record run probes, then resumes.
          committed = null;
          stopped = true;
          return;
        case "complete":
        case "session-gone":
        case "client-error":
        case "protocol-error":
          // `complete` is impossible for a chunk that did not name a total, so
          // it is as untrustworthy as the three genuine refusals.
          dead = true;
          stopped = true;
          return;
      }
    }
  }

  /** Start a drain if one is not already running, there is a session, and a chunk is ready. */
  function kick(): void {
    if (draining !== null || session === null || !ready()) return;
    const url = session.uploadUrl;
    draining = drain(url).finally(() => {
      draining = null;
      // Chunks may have arrived while the last PUT was out. Guarded by
      // `ready()` inside `kick`, or an idle stream would re-enter itself
      // forever on the microtask queue.
      kick();
    });
  }

  return {
    append(chunk: Blob): void {
      if (chunk.size === 0) return;
      chunks.push(chunk);
      buffered += chunk.size;
      kick();
    },

    close(): Promise<LiveUploadHandoff | null> {
      closing ??= (async () => {
        stopped = true;
        // A session still being minted is not waited for. Nothing has been
        // streamed into it, and the post-record run mints its own with bounded
        // retries — whereas an unbounded wait here would hold the finished
        // recording hostage to one slow mutation. The late one is an empty
        // pending session the bucket lifecycle rule reaps.
        if (session === null) dead = true;
        // The drain loop re-kicks itself in `finally`, which `stopped` now
        // refuses, so one await covers it.
        while (draining !== null) await draining;
        if (dead || session === null) return null;
        return {
          uploadUrl: session.uploadUrl,
          key: session.key,
          contentType: params.contentType,
          committed,
        };
      })();
      return closing;
    },

    abandon(): void {
      dead = true;
      stopped = true;
      controller.abort(new UploadAbortedError());
    },
  };
}
